/**
 * سجل صلاحيات الواجهة: ما يتطلبه كل مسار. تستعمله المسارات (الحماية)، والقائمة الجانبية،
 * والإجراءات السريعة، والبحث — فلا يظهر رابط لصفحة ستُرفض.
 *
 * المفاتيح هي نفسها مفاتيح الخلفية (Permission Registry في ASF.Core) ولا تتغير.
 */
import { meets } from "./access";

export const WORK_TYPES = ["Construction", "Maintenance", "Emergency", "NewProject", "PrivateProject"];

const each = (action, types = WORK_TYPES) => types.map((t) => `${t}.${action}`);

export const PROJECT_VIEW = each("View");
export const PROJECT_UPDATE = each("Update");
export const PROJECT_APPROVE = each("Approve");
export const PROJECT_DELETE = each("Delete");
export const PROJECT_CREATE = each("Create");
export const DAILY_EXECUTION = [...PROJECT_UPDATE, ...each("Execute", ["Construction", "Emergency", "Maintenance"])];

const any = (...keys) => ({ anyOf: keys.flat() });
const one = (key) => ({ permission: key });

/** المسار ← الشرط. المسارات غير المذكورة لا تتطلب صلاحية (بعد الدخول). */
export const ROUTE_ACCESS = {
  "/home-page": one("Dashboard.View"),
  "/search-requests": any(PROJECT_VIEW),
  "/admin-projects": any(PROJECT_VIEW),
  "/orders": any(PROJECT_VIEW, "WorkOrderType.View"),
  "/orders/all-types": any(PROJECT_VIEW, "WorkOrderType.View"),
  "/daily-execution": any(DAILY_EXECUTION),
  "/monitoring": one("ContractWorkflow.View"),
  "/pdc-indicators": one("PDCIndicators.View"),
  "/internal-indicators": one("InternalIndicators.View"),
  "/work-orders/new/construction": one("Construction.Create"),
  "/work-orders/new/maintenance": one("Maintenance.Create"),
  "/work-orders/new/emergency": one("Emergency.Create"),
  "/work-orders/new/rehabilitation": one("NewProject.Create"),
  "/work-orders/new/private": one("PrivateProject.Create"),
  "/work-orders/drafts": one("Construction.Create"),
  "/work-orders/import": one("WorkOrders.ExcelImport.View"),
  "/execution-tracking": one("ContractWorkflow.View"),
  // تحديث 60: مركز تحليل ومزامنة Excel وسجل المستخلصات والصرف
  "/work-orders/analysis": any("WorkOrders.ExcelImport.View", "WorkOrders.ExcelAnalysis.View"),
  "/financial-register": any("WorkBudget.View", "WorkBudget.Prepare", "WorkBudget.Approve"),
  // تحديث 59: مركز التواصل لمهندسي المشروعات (أي صلاحية على أوامر العمل/المسارات أو صلاحيات التواصل)؛ الخادم يعزل المشروعات
  "/communication": any(PROJECT_VIEW, DAILY_EXECUTION, "ContractWorkflow.View", "Communication.ManageRooms", "Communication.ViewAllRooms",
    "Circulars.Publish", "Circulars.Reports", "Communication.ViewActivity"),
  "/daily-work-plan": one("DailyWork.View"),
  "/today-work": any("DailyWork.ViewToday", "DailyWork.View"),
  "/execution-teams": any("ExecutionTeams.View", "ExecutionTeams.Manage"),
  "/contract-settings": one("ContractWorkflow.View"),
  "/access-management": one("Users.ManagePermissions"),
  "/engineers": one("Employees.View"),
  "/consultants": one("Consultant.View"),
  "/all-admin-private-project": one("PrivateProject.View"),
  "/deleted-projects": any(PROJECT_DELETE),
  "/accept-requests": any(PROJECT_APPROVE),
  "/accept-delete": any(PROJECT_DELETE),
  "/admin-branch-data": one("Branch.View"),
  "/manager-profile": one("Office.View"),
  "/tasktype": one("WorkOrderType.View"),
  "/add-account": one("Users.Create"),
  "/accounts": one("Users.View"),
  "/notification": one("Notifications.View"),
  "/district": one("Neighborhood.View"),
  "/custodies": one("Custody.View"),
  "/branches": one("Branch.View"),
  "/offices": one("Office.View"),
  "/employees": one("Employees.View"),
  "/vacations": one("LeaveRequest.View"),
  "/check-attendance": one("Attendance.View"),
  "/project-parties": one("ProjectParty.View"),
  "/project-owners": one("ProjectOwner.View"),
  "/profile": null,
  "/home": null,
  // المساعد الذكي: لمن يتعامل مع أوامر العمل (عرض/إنشاء/تعديل/تنفيذ)؛ وكل أداة فيه تتحقق من صلاحياته على الخادم
  "/ai-assistant": any(PROJECT_VIEW, PROJECT_CREATE, DAILY_EXECUTION),
  "/ai-settings": any("AiAssistant.ManageSettings", "AiAssistant.ViewUsage"),
  // خدمات المستخدم نفسه (كانت في واجهة المهندس القديمة) — داخل الهيكل الموحّد لكل الحسابات
  "/eng-attendance": any("Attendance.View", "Attendance.CheckIn"),
  "/myvacations": any("LeaveRequest.View", "LeaveRequest.Create"),
  "/custodieseng": one("Custody.View"),
  "/eng-notification": null,
};

/** شرط مسار (يدعم المسارات ذات المعاملات بأخذ أطول بادئة معروفة). */
export function requirementFor(path) {
  const clean = String(path || "").split("?")[0].replace(/\/+$/, "") || "/";
  if (clean in ROUTE_ACCESS) return ROUTE_ACCESS[clean];
  const prefix = Object.keys(ROUTE_ACCESS)
    .filter((p) => clean.startsWith(p + "/"))
    .sort((a, b) => b.length - a.length)[0];
  return prefix ? ROUTE_ACCESS[prefix] : null;
}

/** هل يُفتح هذا المسار للمستخدم الحالي؟ */
export function canOpen(path) {
  return meets(requirementFor(path));
}
